import { Popover } from '@mui/material';
import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  Button,
  Checkbox,
  ErrorInfo,
  List,
  ListItem,
  MUIDataTable,
  MUIDataTableOptions,
  Progress,
  Typography,
} from 'web-library';
import { useGetAccessRole } from '../../../api-refactored/user/useGetAccessRole';
import { useRoleAccessConfigData, useRoleList } from '../logic/useRoleAccessConfig';
import { AccessMatrixRoleConfiguration } from '../../../api/user/model';

const FEATURE_COLUMN_WIDTH = 200;
const INSTITUTION_COLUMN_WIDTH = 250;

const useStyles = makeStyles()((theme) =>
({
  tableContainer: {
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(3),
  },
  table: {
    minWidth: 650,
  },
  stickyCell: {
    position: 'sticky',
    maxWidth: INSTITUTION_COLUMN_WIDTH,
    minWidth: INSTITUTION_COLUMN_WIDTH,
    left: 0,
    zIndex: 1,
    backgroundColor: theme.color.background.bgMain,
  },
  checkboxContainer: {
    width: '100%',
  },
  featureCell: {
    maxWidth: FEATURE_COLUMN_WIDTH,
    minWidth: FEATURE_COLUMN_WIDTH,
  },
  noDataTableBody: {
    width: '100%',
    backgroundColor: 'pink',
    height: 100,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  addRemoveRoleButtonsContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
  },
  addRemoveRoleButton: {
    marginRight: theme.spacing(2),
  },
  submitButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    marginTop: theme.spacing(2),
  },
}));

const muiDataTableOptions: MUIDataTableOptions = {
  elevation: 4,
  filter: false,
  confirmFilters: false,
  download: false,
  print: false,
  viewColumns: false,
  fixedHeader: true,
  search: false,
  selectableRows: 'none',
  serverSide: true,
  // tableBodyHeight: '400px',
  responsive: 'standard',
  customFooter: () => <div />,
  customToolbarSelect: () => <div />,
};

const AccessMatrixCheckbox = ({
  individualAccessMatrix,
  moduleId,
  onClickToggleAccess,
}: {
  individualAccessMatrix: AccessMatrixRoleConfiguration;
  moduleId: number;
  onClickToggleAccess: (roleId: number, moduleId: number) => void;
}) => {
  const { classes } = useStyles();
  return (
    <Box
      className={classes.checkboxContainer}
      onClick={() => onClickToggleAccess(individualAccessMatrix.id, moduleId)}
    >
      <Checkbox checked={individualAccessMatrix.modules.includes(moduleId)} ariaLabel="" />
    </Box>
  );
};

const AdminAccessRole = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const { data: accessRoleApiData, isLoading, error } = useGetAccessRole();

  // get data to be displayed in table and handle user interactions e.g. toggle checkbox etc. at table
  const {
    accessMatrix,
    onClickToggleAccess,
    onClickAddRole,
    onClickRemoveRole,
    onClickSubmit,
    accessConfigMutator,
    hasUserMadeChanges,
  } = useRoleAccessConfigData(accessRoleApiData?.accessMatrixConfiguration);

  // handle display for adding/removing institution to/from table
  const { availableRolesToAdd, availableRolesToDelete } = useRoleList(
    accessMatrix,
    accessRoleApiData?.roles
  );

  const [addRoleAnchorEl, setAddRoleAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isAddRoleOpen = Boolean(addRoleAnchorEl);

  const [removeRoleAnchorEl, setRemoveRoleAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isRemoveRoleOpen = Boolean(removeRoleAnchorEl);

  if (isLoading) {
    return <Progress color="secondary" />;
  }

  if (error || !accessRoleApiData) {
    return <Typography>Error getting access role data.</Typography>;
  }

  const muiDataTableColumns: any = {
    name: 'role',
    label: 'User Group',
    options: {
      sort: true,
      searchable: true,
      fixedHeader: true,
      setCellProps: () => ({
        style: {
          whiteSpace: 'nowrap',
          position: 'sticky',
          left: 0,
          zIndex: 100,
        },
      }),
      setCellHeaderProps: () => ({
        style: {
          whiteSpace: 'nowrap',
          position: 'sticky',
          left: 0,
          zIndex: 101,
        },
      }),

      customBodyRenderLite: (dataIndex: number) =>
        accessRoleApiData.roles.find((role) => role.id === accessMatrix[dataIndex].id)?.name,
    },
  };

  const otherColumn: any = accessRoleApiData.modules.map((module) => ({
    name: module.name,
    label: module.name,
    options: {
      sort: false,
      searchable: false,

      customBodyRenderLite: (dataIndex: number) =>
        AccessMatrixCheckbox({
          individualAccessMatrix: accessMatrix[dataIndex],
          moduleId: module.id,
          onClickToggleAccess,
        }),
    },
  }));

  const columns = [muiDataTableColumns, ...otherColumn];

  return (
    <>
      <Box className={classes.addRemoveRoleButtonsContainer}>
        <Button
          variant="outlined"
          className={classes.addRemoveRoleButton}
          size="small"
          onClick={(event) => setAddRoleAnchorEl(event ? event.currentTarget : null)}
          disabled={availableRolesToAdd.length < 1}
        >
          Add Role
        </Button>
        <Popover
          open={isAddRoleOpen}
          onClose={() => setAddRoleAnchorEl(null)}
          anchorEl={addRoleAnchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <List>
            {availableRolesToAdd.map((institutionKey) => (
              <ListItem
                key={institutionKey.id}
                button
                onClick={() => {
                  onClickAddRole(institutionKey.id);
                  setAddRoleAnchorEl(null);
                }}
              >
                {institutionKey.name}
              </ListItem>
            ))}
          </List>
        </Popover>
        <Button
          variant="outlined"
          size="small"
          onClick={(event) => setRemoveRoleAnchorEl(event ? event.currentTarget : null)}
          disabled={availableRolesToDelete.length < 1}
        >
          Remove Role
        </Button>
        <Popover
          open={isRemoveRoleOpen}
          onClose={() => setRemoveRoleAnchorEl(null)}
          anchorEl={removeRoleAnchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <List>
            {availableRolesToDelete.map((institutionKey) => (
              <ListItem
                key={institutionKey.id}
                button
                onClick={() => {
                  onClickRemoveRole(institutionKey.id);
                  setRemoveRoleAnchorEl(null);
                }}
              >
                {institutionKey.name}
              </ListItem>
            ))}
          </List>
        </Popover>
      </Box>

      {Object.entries(accessMatrix).length > 0 ? (
        <MUIDataTable
          columns={columns}
          data={accessMatrix}
          options={muiDataTableOptions}
        />
      ) : (
        <Typography>Click On the Add Button to Add new Role</Typography>
      )}

      <Box className={classes.submitButtonContainer}>
        <ApiButton
          id="access-configure"
          status={accessConfigMutator.status}
          onClick={() => {
            onClickSubmit();
          }}
          disabled={!hasUserMadeChanges}
        >
          {t('admin->>AdminAccessRole.Submit')}
        </ApiButton>
      </Box>
      {accessConfigMutator.error && <ErrorInfo label={accessConfigMutator.error?.message || ''} />}
    </>
  );
};

export default AdminAccessRole;
